import api from '@/modules/api.service';
import { RelatorioAtividadeDto } from './relatorios-atividades.service';

export interface CorregedoriaUsuarioInfo {
  isCorregedoria: boolean;
  nome?: string;
  setorNome?: string;
}

/**
 * Verifica se o usuário logado pertence à Corregedoria (lotação com setor_id = 3).
 * Considera defensor ou colaborador com lotação no setor Corregedoria.
 */
export async function CorregedoriaCheck(): Promise<boolean> {
  try {
    const info = await GetCorregedoriaUsuarioInfo();
    return info.isCorregedoria;
  } catch {
    return false;
  }
}

/**
 * Retorna se o usuário é Corregedoria e, quando for, nome e setor da pessoa logada (para exibir na tela).
 */
export async function GetCorregedoriaUsuarioInfo(): Promise<CorregedoriaUsuarioInfo> {
  try {
    const response = await api.get<CorregedoriaUsuarioInfo>('/relatorios/atividades/corregedoria/check');
    return response.data ?? { isCorregedoria: false };
  } catch {
    return { isCorregedoria: false };
  }
}

/**
 * Lista relatórios para a Corregedoria (aprovados pelo diretor).
 * apenasAguardando = true: só os que aguardam aval; false: todos (inclui os que já receberam aval).
 */
export async function BuscarRelatoriosParaCorregedoria(filters?: {
  defensorId?: number;
  defensoriaId?: number;
  mes?: string;
  ano?: number;
  tipo?: 'civil' | 'criminal';
  apenasAguardando?: boolean;
}): Promise<RelatorioAtividadeDto[]> {
  try {
    const params: Record<string, unknown> = {};

    if (filters?.defensorId) params.defensorId = filters.defensorId;
    if (filters?.defensoriaId) params.defensoriaId = filters.defensoriaId;
    if (filters?.mes) params.mes = filters.mes;
    if (filters?.ano) params.ano = filters.ano;
    if (filters?.tipo) params.tipo = filters.tipo;
    if (filters?.apenasAguardando !== undefined) {
      params.apenasAguardando = filters.apenasAguardando ? 'true' : 'false';
    }

    const response = await api.get('/relatorios/atividades/corregedoria', { params });
    const relatorios = response.data || [];

    return relatorios.map((r: any) => ({
      ...r,
      dataCriacao: r.criadoEm ? new Date(r.criadoEm).toISOString() : '',
      dataEnvio: r.dataEnvio ? new Date(r.dataEnvio).toISOString() : undefined,
      dataAprovacao: r.dataAprovacao ? new Date(r.dataAprovacao).toISOString() : undefined,
      dataReprovacao: r.dataReprovacao ? new Date(r.dataReprovacao).toISOString() : undefined,
      corregedoriaAprovadoEm: r.corregedoriaAprovadoEm ? new Date(r.corregedoriaAprovadoEm).toISOString() : undefined,
      corregedoriaAprovadoPorNome: r.corregedoriaAprovadoPorNome,
      dataAtualizacao: r.editadoEm ? new Date(r.editadoEm).toISOString() : undefined,
    }));
  } catch (error) {
    console.error('Erro ao buscar relatórios para Corregedoria:', error);
    throw error;
  }
}

/** Item para informar quais dias "Remoto" a corregedoria valida como presença */
export interface RemotoValidadoItem {
  dia: number;
  mes: string;
  ano: number;
}

export type ComparecimentoDecisaoAcao =
  | 'remoto_validar'
  | 'remoto_rejeitar'
  | 'ausencia_aceitar'
  | 'ausencia_rejeitar';

export interface ComparecimentoDiaDecisao {
  dia: number;
  mes: string;
  ano: number;
  acao: ComparecimentoDecisaoAcao;
}

function mapRelatorioAtividadeResponse(relatorio: any): RelatorioAtividadeDto {
  return {
    ...relatorio,
    dataCriacao: relatorio.criadoEm ? new Date(relatorio.criadoEm).toISOString() : '',
    dataEnvio: relatorio.dataEnvio ? new Date(relatorio.dataEnvio).toISOString() : undefined,
    dataAprovacao: relatorio.dataAprovacao ? new Date(relatorio.dataAprovacao).toISOString() : undefined,
    dataReprovacao: relatorio.dataReprovacao ? new Date(relatorio.dataReprovacao).toISOString() : undefined,
    corregedoriaAprovadoEm: relatorio.corregedoriaAprovadoEm
      ? new Date(relatorio.corregedoriaAprovadoEm).toISOString()
      : undefined,
    dataAtualizacao: relatorio.editadoEm ? new Date(relatorio.editadoEm).toISOString() : undefined,
  };
}

/** Persiste decisões da Corregedoria por dia (remoto/ausência), antes do aval final. */
export async function AplicarDecisoesComparecimentoCorregedoria(relatorioId: number, decisoes: ComparecimentoDiaDecisao[]): Promise<RelatorioAtividadeDto> {
  const response = await api.post(`/relatorios/atividades/${relatorioId}/corregedoria/comparecimento-dias`, { decisoes });
  return mapRelatorioAtividadeResponse(response.data);
}

/**
 * Dar aval final da Corregedoria (aprovar relatório já aprovado pelo diretor).
 * remotoValidados: dias com status "Remoto" que a corregedoria valida como presença; omitir ou [] = nenhum validado.
 */
export async function AprovarCorregedoria(relatorioId: number, remotoValidados?: RemotoValidadoItem[]): Promise<RelatorioAtividadeDto> {

  const body = remotoValidados && remotoValidados.length > 0 ? { remotoValidados } : {};
  const response = await api.post(`/relatorios/atividades/${relatorioId}/corregedoria/aprovar`, body);
  return mapRelatorioAtividadeResponse(response.data);
}

/**
 * Corregedoria reprova: pode devolver ao diretor (destino 'diretor') ou ao defensor (destino 'defensor' ou omitido).
 */
export async function ReprovarCorregedoria(relatorioId: number, justificativa: string, destino?: 'diretor' | 'defensor'): Promise<RelatorioAtividadeDto> {

  if (!justificativa?.trim()) {
    throw new Error('Justificativa é obrigatória para reprovação pela Corregedoria.');
  }

  const body: { justificativa: string; destino?: 'diretor' | 'defensor' } = {
    justificativa: justificativa.trim(),
  };

  if (destino)
    body.destino = destino;

  const response = await api.post(`/relatorios/atividades/${relatorioId}/corregedoria/reprovar`, body);
  return mapRelatorioAtividadeResponse(response.data);
}
